/**
 * Administração ▸ 🩺 Vigia (12/09): o histórico das verificações semanais e
 * o botão "Rodar agora". Cada execução abre com as sondas, as checagens que
 * acusaram algo (com amostra), os indicadores e o texto da IA.
 */

import { api, ErroApi } from '../../api.js';
import { escapar, dataHora } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';

interface Sonda { nome: string; titulo: string; ok: boolean; aviso?: boolean; detalhe: string; ms: number }
interface Checagem { nome: string; titulo: string; explicacao: string; gravidade: 'alta' | 'media' | 'baixa'; linhas: number; amostra: Record<string, unknown>[]; erro?: string }
interface Execucao {
  id: number;
  iniciado_em: string;
  terminado_em: string | null;
  disparo: string;
  ok: boolean | null;
  alertas: number;
  resultado: { sondas?: Sonda[]; checagens?: Checagem[]; indicadores?: Record<string, { titulo: string; valor: number | null }>; duracaoMs?: number; versao?: string };
  proposta: string | null;
  email_enviado: boolean;
}

export function montarVigia(raiz: HTMLElement): void {
  raiz.innerHTML = `<p class="carregando">Carregando o vigia…</p>`;

  const desenhar = async (): Promise<void> => {
    let dados: { execucoes: Execucao[]; batidaHoraria: { em: string } | null; checagens: { nome: string; titulo: string; gravidade: string }[] };
    try {
      dados = await api.get('/api/vigia');
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(erro instanceof ErroApi ? erro.message : 'Não consegui carregar.')}</p>`;
      return;
    }
    const ultima = dados.execucoes[0];
    raiz.innerHTML = `
      <section class="ped-painel">
        <header class="ficha-topo"><div>
          <h2>🩺 Vigia do sistema</h2>
          <span class="sub-inline">Todo sábado às 00:30 ele testa as dependências (IA, banco, fotos, e-mail, rastreamento…), confere o banco com ${dados.checagens.length} checagens e a IA escreve o diagnóstico com propostas. O relatório vai por e-mail.</span>
        </div></header>
        <p class="dica">Batida horária do agendador: ${dados.batidaHoraria ? `última às <b>${dataHora(dados.batidaHoraria.em)}</b>` : '<b>nenhuma registrada ainda</b>'}
          · última verificação: ${ultima ? `<b>${dataHora(ultima.iniciado_em)}</b> — ${ultima.ok ? '✅ tudo em ordem' : ultima.ok === null ? '⏳ em andamento' : `❌ ${ultima.alertas} alerta(s)`}` : '<b>nunca rodou</b>'}</p>
        <div class="conf-acoes"><button class="btn" id="vg-rodar" type="button">▶️ Rodar agora</button>
          <span class="sub-inline">leva de 30 s a 2 min; o resultado aparece abaixo e vai por e-mail.</span></div>
      </section>
      ${dados.execucoes.map(cartao).join('') || '<p class="dica">Nenhuma execução ainda.</p>'}`;

    raiz.querySelector<HTMLButtonElement>('#vg-rodar')!.addEventListener('click', (ev) => {
      const b = ev.currentTarget as HTMLButtonElement;
      b.disabled = true;
      b.textContent = '⏳ Verificando… (até 2 min)';
      void api
        .post<{ ok: boolean; alertas: number }>('/api/vigia/rodar', {})
        .then((r) => {
          avisar(r.ok ? 'Verificação concluída — tudo em ordem.' : `Verificação concluída com ${r.alertas} alerta(s).`, r.ok ? 'ok' : 'atencao');
          return desenhar();
        })
        .catch((e) => {
          b.disabled = false;
          b.textContent = '▶️ Rodar agora';
          avisarBloqueante(e instanceof ErroApi ? e.message : 'A verificação falhou.');
        });
    });
    raiz.querySelectorAll<HTMLElement>('[data-vg-abrir]').forEach((h) => {
      h.addEventListener('click', () => {
        const corpo = raiz.querySelector<HTMLElement>(`#vg-corpo-${h.dataset['vgAbrir']}`)!;
        corpo.hidden = !corpo.hidden;
      });
    });
  };

  const cartao = (e: Execucao, i: number): string => {
    const r = e.resultado ?? {};
    const sondas = r.sondas ?? [];
    const problemas = (r.checagens ?? []).filter((c) => c.linhas > 0 || c.erro);
    const limpas = (r.checagens ?? []).length - problemas.length;
    const gravidadeIcone = (c: Checagem) => (c.erro ? '❌' : c.gravidade === 'alta' ? '🔴' : c.gravidade === 'media' ? '🟠' : '🟡');
    return `
      <article class="vg-exec ${e.ok === false ? 'vg-alerta' : ''}">
        <header class="vg-cabecalho" data-vg-abrir="${e.id}" role="button" tabindex="0">
          <b>${e.ok === null ? '⏳' : e.ok ? '✅' : '❌'} ${dataHora(e.iniciado_em)}</b>
          <span class="sub-inline">${e.disparo === 'manual' ? 'manual' : 'sábado 00:30'} · ${e.ok === null ? 'em andamento' : e.ok ? 'tudo em ordem' : `${e.alertas} alerta(s)`}
            ${r.duracaoMs ? ` · ${(r.duracaoMs / 1000).toFixed(0)} s` : ''}${r.versao ? ` · versão ${escapar(r.versao)}` : ''}${e.email_enviado ? ' · 📧 e-mail enviado' : ''}</span>
        </header>
        <div id="vg-corpo-${e.id}" ${i === 0 ? '' : 'hidden'}>
          ${e.proposta ? `<div class="vg-proposta">${escapar(e.proposta)}</div>` : '<p class="dica">Sem texto da IA nesta execução.</p>'}
          <h4>Sondas</h4>
          <table class="tabela vg-tabela"><tbody>${sondas
            .map((s) => `<tr><td>${s.ok ? (s.aviso ? '⚠️' : '✅') : '❌'}</td><td>${escapar(s.titulo)}</td><td>${escapar(s.detalhe)}</td><td class="num">${(s.ms / 1000).toFixed(1)} s</td></tr>`)
            .join('')}</tbody></table>
          <h4>Checagens do banco <span class="sub-inline">${limpas} passaram${problemas.length ? `, ${problemas.length} acusaram` : ''}</span></h4>
          ${
            problemas.length
              ? problemas
                  .map(
                    (c) => `<div class="vg-checagem">
                      <b>${gravidadeIcone(c)} ${escapar(c.titulo)}</b> — ${c.erro ? `erro: ${escapar(c.erro)}` : `${c.linhas} linha(s)`}
                      <div class="sub-inline">${escapar(c.explicacao)}</div>
                      ${
                        c.amostra?.length
                          ? `<div class="rolagem"><table class="tabela vg-tabela"><thead><tr>${Object.keys(c.amostra[0]!)
                              .map((k) => `<th>${escapar(k)}</th>`)
                              .join('')}</tr></thead><tbody>${c.amostra
                              .map((l) => `<tr>${Object.values(l).map((v) => `<td>${escapar(v === null || v === undefined ? '—' : String(v))}</td>`).join('')}</tr>`)
                              .join('')}</tbody></table></div>`
                          : ''
                      }
                    </div>`,
                  )
                  .join('')
              : '<p class="dica">Nenhuma inconsistência encontrada.</p>'
          }
          <h4>Indicadores da semana</h4>
          <table class="tabela vg-tabela"><tbody>${Object.values(r.indicadores ?? {})
            .map((x) => `<tr><td>${escapar(x.titulo)}</td><td class="num">${x.valor === null ? '—' : x.valor.toLocaleString('pt-BR')}</td></tr>`)
            .join('')}</tbody></table>
        </div>
      </article>`;
  };

  void desenhar();
}
